<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, DollarSign, RefreshCw, ChevronLeft, ChevronRight, History, Search, Info } from 'lucide-vue-next';
import { storeOrdersApiClient } from '@api-client/store_orders';
import type { ResType } from '@api-client/typeUtils';
import { formatCurrency } from '@lib/utils/formatCurrency';
import { formatDate } from '@lib/utils/formatDate';
import { Input } from '@/components/ui/input';

const props = defineProps<{
    currentStoreId: string;
    searchQuery?: string;
}>();

const isLoading = ref(false);

type StoreBalance = ResType<typeof storeOrdersApiClient.balance_get[':storeId']['$get']>["data"];

const historyApiPath = storeOrdersApiClient.balance_history[':storeId'].$post;
type StoreBalanceHistoryResponse = ResType<typeof historyApiPath>["data"];
type StoreBalanceHistoryItem = StoreBalanceHistoryResponse["items"][0];

const balances = ref<StoreBalance["balances"]>([]);
const cancelPercentage = ref<StoreBalance["cancelPercentage"]>(0);
const historyItems = ref<StoreBalanceHistoryItem[]>([]);
const isHistoryLoading = ref(false);
const hasNextPage = ref(false);
const currentCursor = ref<{ created_at_ts: number; id: string } | null>(null);
const cursors = ref<Array<{ created_at_ts: number; id: string } | null>>([null]); // Stack of cursors for pagination
const currentPageIndex = ref(0);
const searchQuery = ref(props.searchQuery || '');

const reloadAll = async () => {
    await loadBalance();
    cursors.value = [null];
    currentPageIndex.value = 0;
    await loadHistoryData();
}

const searchFromStart = async () => {
    cursors.value = [null];
    currentPageIndex.value = 0;
    currentCursor.value = null;
    await loadHistoryData();
}

const resetSearch = () => {
    searchQuery.value = '';
    searchFromStart();
}

onMounted(() => {
    reloadAll();
});

async function loadBalance() {
    isLoading.value = true;
    try {
        const { data, error } = await (await storeOrdersApiClient.balance_get[':storeId'].$get({
            param: {
                storeId: props.currentStoreId || ''
            }
        })).json();

        if (error) {
            console.error('Failed to load balance: ' + error.message);
        } else {
            balances.value = data.balances || [];
            cancelPercentage.value = data.cancelPercentage;
        }
    } catch (error: any) {
        console.error('Failed to load balance:', error);
    } finally {
        isLoading.value = false;
    }
}

async function loadHistoryData(cursor?: { created_at_ts: number; id: string } | null) {
    isHistoryLoading.value = true;
    try {
        const { data, error } = await (await storeOrdersApiClient.balance_history[':storeId'].$post({
            param: {
                storeId: props.currentStoreId || ''
            },
            json: {
                cursor: cursor || undefined,
                pageSize: 10,
                search: searchQuery.value.trim() || undefined,
            }
        })).json();

        if (error) {
            console.error('Failed to load balance history: ' + error.message);
        } else {
            historyItems.value = data?.items || [];
            hasNextPage.value = data?.hasNextPage || false;
            currentCursor.value = data?.nextCursor || null;
        }
    } catch (error: any) {
        console.error('Failed to load balance history:', error);
    } finally {
        isHistoryLoading.value = false;
    }
}

function goToNextPage() {
    if (hasNextPage.value && currentCursor.value) {
        cursors.value.push(currentCursor.value);
        currentPageIndex.value++;
        loadHistoryData(currentCursor.value);
    }
}

function goToPrevPage() {
    if (currentPageIndex.value > 0) {
        cursors.value.pop();
        currentPageIndex.value--;
        const prevCursor = cursors.value[cursors.value.length - 1];
        loadHistoryData(prevCursor);
    }
}


</script>

<template>
    <div class="space-y-4">
        <details class="rounded-lg border p-4">
            <summary class="cursor-pointer font-semibold">Info
                <Info class="h-4 w-4 inline-block" />
            </summary>
            <div class="mt-3 space-y-3 text-sm text-muted-foreground leading-relaxed">
                <p>
                    The fees shown at checkout are added to the customer's order. Depending on the payment method, we
                    collect these fees differently:
                </p>
                <ul class="list-disc space-y-1 pl-5">
                    <li>
                        <span class="font-medium text-foreground">Stripe payments:</span>
                        We automatically collect our fees from the payment.
                    </li>
                    <li>
                        <span class="font-medium text-foreground">Cash and other payment methods:</span>
                        We cannot collect the fees automatically. The extra amount is collected by you from the
                        customer, and you are responsible for paying it to us.
                    </li>
                </ul>
                <p>This page shows your current balance:</p>
                <ul class="list-disc space-y-1 pl-5">
                    <li>
                        <span class="font-medium text-foreground">Positive balance:</span>
                        You have credit that will cover future fees.
                    </li>
                    <li>
                        <span class="font-medium text-foreground">Negative balance:</span>
                        You owe us this amount.
                    </li>
                    <li>
                        <span class="font-medium text-foreground">Zero balance:</span>
                        Everything is settled.
                    </li>
                </ul>
                <p>Your balance is updated automatically as orders are processed.</p>
                <p>
                    If your balance becomes negative, we will automatically collect the amount from your next Stripe
                    payments to maintain a minimum positive balance.
                </p>
            </div>
        </details>

        <!-- Balance Details -->
        <Card>
            <CardHeader>
                <CardTitle>
                    Balance Details
                    <Badge variant="outline"
                           class="text-xs">
                        Cancel Rate: {{ (cancelPercentage ?? 0).toFixed(0) }}% (last 30 days)
                    </Badge>
                </CardTitle>
                <CardDescription>
                    <div class="flex justify-between items-center">
                        <RefundPolicy />
                        <Button @click="reloadAll"
                                :disabled="isLoading"
                                variant="outline"
                                class="mr-2">
                            <RefreshCw :class="{ 'animate-spin': isLoading }"
                                       class="h-4 w-4 mr-2" />
                            Refresh
                        </Button>
                    </div>
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div class="space-y-4">

                    <!-- No Data State -->
                    <div v-if="!balances || balances.length === 0"
                         class="text-center py-12">
                        <DollarSign class="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <h3 class="text-lg font-semibold mb-2">No Balance Data</h3>
                        <p class="text-muted-foreground mb-4">
                            No balance records found for this store. Balances will appear once you start processing
                            orders.
                        </p>
                    </div>

                    <div v-for="balance in balances"
                         :key="`${balance.storeId}-${balance.currency}`"
                         class="flex items-center justify-between p-4 border rounded-lg">
                        <div class="flex items-center space-x-4">
                            <div class="flex items-center space-x-2">
                                <Badge variant="outline"
                                       class="font-mono">
                                    {{ balance.currency }}
                                </Badge>
                            </div>
                            <div>
                                <div class="text-sm text-muted-foreground">
                                    Last updated: {{ formatDate(balance.updated_at_ts, undefined, true) }}
                                </div>
                            </div>
                        </div>
                        <div class="text-right">
                            <div :class="[
                                'text-lg font-semibold whitespace-nowrap',
                                balance.balance > 0 ? 'text-green-600' :
                                    balance.balance < 0 ? 'text-red-600' : 'text-muted-foreground'
                            ]">
                                {{ formatCurrency(balance.balance, balance.currency) }}
                            </div>
                            <Badge :variant="balance.balance > 0 ? 'default' : balance.balance < 0 ? 'destructive' : 'secondary'"
                                   class="text-xs">
                                {{ balance.balance > 0 ? 'Credit' : balance.balance < 0 ? 'Debt' : 'Zero' }}
                            </Badge>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>

        <!-- Balance History -->
        <Card>
            <CardHeader>
                <div class="flex items-center justify-between">
                    <div>
                        <CardTitle class="flex items-center gap-2">
                            <History class="h-5 w-5" />
                            Balance History
                        </CardTitle>
                        <CardDescription>
                            Recent transactions affecting your store balance (limit 10 per page)
                            <!-- <p>
                                (sales taxes are always included shown amounts)
                            </p> -->
                        </CardDescription>
                    </div>
                </div>
                <form class="flex gap-2 mt-4"
                      @submit.prevent="searchFromStart">
                    <Input v-model="searchQuery"
                           placeholder="Search by order # or order ID"
                           :disabled="isHistoryLoading" />
                    <Button type="submit"
                            variant="outline"
                            :disabled="isHistoryLoading">
                        <Loader2 v-if="isHistoryLoading"
                                 class="h-4 w-4 animate-spin" />
                        <Search v-else
                                class="h-4 w-4" />
                    </Button>
                </form>
                <Button v-if="searchQuery"
                        @click="resetSearch"
                        variant="outline"
                        class="mt-2">
                    Clear Search
                </Button>
            </CardHeader>
            <CardContent>
                <!-- Loading State -->
                <div v-if="isHistoryLoading && historyItems.length === 0"
                     class="flex justify-center items-center py-8">
                    <Loader2 class="h-6 w-6 animate-spin" />
                    <span class="ml-2">Loading history...</span>
                </div>

                <!-- No Data State -->
                <div v-else-if="!historyItems || historyItems.length === 0"
                     class="text-center py-8">
                    <History class="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p class="text-muted-foreground">No transaction history found</p>
                </div>

                <!-- History Data -->
                <div v-else
                     class="space-y-3">
                    <div v-for="item in historyItems"
                         :key="item.storeAtomicBalanceTransactionId"
                         class="p-3 border rounded-lg">

                        <!-- Mobile and Desktop Responsive Layout -->
                        <div class="flex flex-col gap-3">

                            <!-- Amounts Section (below details, always mobile layout) -->
                            <div class="flex flex-wrap items-start justify-between gap-3">
                                <div class="space-y-1">
                                    <div class="text-xs text-muted-foreground">Amount</div>
                                    <div :class="[
                                        ' font-semibold',
                                        item.amount > 0 ? 'text-green-600' :
                                            item.amount < 0 ? 'text-red-600' : 'text-muted-foreground'
                                    ]">
                                        {{ formatCurrency(item.amount || 0, item.currency || 'GBP') }}
                                        <Badge v-if="!item.isCharged"
                                               variant="destructive"
                                               class="text-xs">
                                            Unpaid
                                        </Badge>
                                        <Badge v-if="item.isCharged"
                                               variant="outline"
                                               class="text-xs">
                                            Paid
                                        </Badge>
                                    </div>
                                </div>

                                <div class="space-y-1 text-right">
                                    <div class="text-xs text-muted-foreground">Ending Balance</div>
                                    <div class="font-semibold">
                                        {{ formatCurrency(item.endingBalance || 0, item.currency || 'GBP') }}
                                    </div>
                                </div>
                            </div>

                            <!-- Details Section (always mobile view) -->
                            <div class="flex justify-between items-center">
                                <div class="space-y-1">
                                    <div class="flex flex-wrap items-center gap-2">
                                        <Badge variant="outline"
                                               class="text-xs">
                                            {{ item.type }}
                                        </Badge>
                                        <Badge v-if="item.type === 'ORDER_CANCELLED'"
                                               variant="destructive"
                                               class="text-xs">
                                            Cancelled
                                        </Badge>
                                        <Badge variant="outline"
                                               class="text-xs"
                                               v-if="item.shortOtp">
                                            Order: #{{ item.shortOtp }}
                                        </Badge>
                                        <Badge variant="outline"
                                               class="text-xs"
                                               v-if="item.storeOrderId">
                                            {{ item.storeOrderId }}
                                        </Badge>
                                    </div>
                                    <div class="text-xs">
                                        {{ formatDate(item.created_at_ts, undefined, true) }}
                                    </div>
                                    <details class="text-xs text-muted-foreground">
                                        <summary class="cursor-pointer">Breakdown</summary>
                                        <div class="text-xs overflow-y-auto h-32 max-h-32">
                                            <pre class="whitespace-pre-wrap h-full">{{ item.breakdown }}</pre>
                                        </div>
                                    </details>
                                </div>
                                <div class="space-y-1 text-right">
                                    <div class="text-xs text-muted-foreground">Net</div>
                                    <div class="font-semibold">
                                        {{ formatCurrency(item.net || 0, item.currency || 'GBP') }}
                                    </div>
                                </div>
                            </div>



                        </div>

                    </div>

                    <!-- Pagination Controls -->
                    <div class="flex items-center justify-between pt-4">
                        <div class="text-sm text-muted-foreground">
                            Page {{ currentPageIndex + 1 }}
                        </div>
                        <div class="flex gap-2">
                            <Button @click="goToPrevPage"
                                    :disabled="currentPageIndex === 0 || isHistoryLoading"
                                    variant="outline"
                                    size="sm">
                                <ChevronLeft class="h-4 w-4 mr-1" />
                                Previous
                            </Button>
                            <Button @click="goToNextPage"
                                    :disabled="!hasNextPage || isHistoryLoading"
                                    variant="outline"
                                    size="sm">
                                Next
                                <ChevronRight class="h-4 w-4 ml-1" />
                            </Button>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    </div>
</template>